import Header from '@/components/header';
import { Button } from '@/components/ui/button';
import { useBelowMinimumOs } from '@/hooks/useBelowMinimumOs';
import { localeAtom, markedCompleteStepIdsAtom, sessionAtom } from '@/lib/atoms';
import { countPhotosByStepId, nextStep } from '@transpordiamet/shared';
import { addLangParam, getLocalizedStepField } from '@/lib/utils';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useAtomValue } from 'jotai';
import { Check } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const StepsPage = () => {
  const session = useAtomValue(sessionAtom);
  const locale = useAtomValue(localeAtom);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Checkmarks are visual. Optional steps stay numbered until they have a photo.
  const markedCompleteStepIds = useAtomValue(markedCompleteStepIdsAtom);
  const belowMinimumOs = useBelowMinimumOs();

  const onStartClick = async () => {
    if (!session?.configuration.steps || session.configuration.steps.length === 0) return;

    const next = nextStep(session.configuration.steps, undefined, countPhotosByStepId(session.photos));
    if (next) {
      navigate(addLangParam(`steps/${next.id}`, searchParams));
    } else {
      // All steps completed, go to success page
      navigate(addLangParam('success', searchParams));
    }
  };

  return (
    <>
      <div className="min-h-screen bg-background p-8 pb-32">
        <Header />

        <p
          className="mt-6 max-w-4xl mx-auto rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-base text-blue-950"
          role="note"
        >
          <Trans>Turn the device to a horizontal position and follow the instructions on the screen.</Trans>
        </p>

        {belowMinimumOs && (
          <p
            className="mt-3 max-w-4xl mx-auto rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-base text-amber-950"
            role="alert"
          >
            <Trans>Your device or browser may not be supported. Use iOS 17 or Android 13 or newer.</Trans>
          </p>
        )}

        <div className="mt-6 space-y-1 max-w-4xl mx-auto bg-gray-50 p-2 rounded-lg">
          {session?.configuration?.steps?.map((step, index) => {
            const isCompleted = markedCompleteStepIds.has(step.id);
            const stepTitle = getLocalizedStepField(step, 'title', locale);

            return (
              <div
                key={step.id}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md ${
                  isCompleted ? 'bg-green-50 border border-green-200' : 'bg-white border border-gray-200'
                }`}
              >
                <div
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                    isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {isCompleted ? <Check className="w-3.5 h-3.5" /> : index + 1}
                </div>

                <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">{stepTitle}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Floating button */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-white via-white to-transparent z-50">
        <div className="max-w-4xl mx-auto">
          <Button
            onClick={onStartClick}
            size="lg"
            className="w-full bg-black hover:bg-gray-800 text-white text-lg py-6 rounded-xl shadow-lg transition-all hover:scale-[1.02]"
          >
            {t`Start taking photos`}
          </Button>
        </div>
      </div>
    </>
  );
};

export default StepsPage;
